import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import { Button, TextField, Grid, Box, Autocomplete } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  address_id: Yup.string().required("Address is required"),
  asset_name: Yup.string().required("Asset name is required"),
  asset_sku: Yup.string().required("Asset SKU is required"),
  customer_id: Yup.string().required("Customer is required"),
  asset_purchased_date: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD required")
    .required("Purchased date is required")
    .test(
      "is-valid-date",
      "Invalid date",
      (value) => !Number.isNaN(new Date(value).getTime())
    ),
  asset_type_id: Yup.string().required("Asset Type is required"),
});

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});
interface UploadedFile {
  name: string;
  type: string;
  size: number;
  preview: string;
}
interface FormValues {
  address_id: string;
  customer_id: string;
  division_id: string | null;
  asset_name: string;
  asset_sku: string;
  asset_purchased_date: string;
  asset_type_id: string;
  asset_image?: string;
  image_type?: string;
}
interface AssetType {
  asset_type_id: string;
  asset_type_name: string;
}
interface Asset {
  address_id: string;
  asset_name: string;
  asset_sku: string;
  customer_id: string;
  division_id: string;
  asset_purchased_date: string | number | Date;
  asset_type_id: string;
  asset_id: string;
  customer_name: string;
  division_name: string;
  type: string;
  customer_phone: string;
  city_name: string;
  region_name: string;
  address_type: string;
  image_type: string;
  asset_image: string;
}
interface EditAssetProps {
  assetData: Asset | null;
  fetchData: () => Promise<void>;
  onCancel: () => void;
}
const EditAsset = ({ assetData, fetchData, onCancel }: EditAssetProps) => {
  console.log("assetData", assetData);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [assets, setAssets] = useState<AssetType[]>([]);

  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  const dropzone = useDropzone({
    accept: { "image/png": [".png", ".jpeg", ".jpg"], "video/mp4": [".mp4"] },
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, { preview: URL.createObjectURL(file) })
        ),
      ]);
    },
  });

  useEffect(() => {
    const filesWithPreview = dropzone.acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    ) as UploadedFile[];

    setUploadedFiles(filesWithPreview);
  }, [dropzone.acceptedFiles]);
  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });
      setAssets(response.data.list ?? []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Helper function to render media preview - fixes nested ternary issue
  const renderMediaPreview = () => {
    // Case 1: Newly uploaded files
    if (uploadedFiles.length > 0) {
      const file = uploadedFiles[0];
      const isImage = file.type.startsWith("image");

      if (isImage) {
        return (
          <img
            src={file.preview}
            alt="preview"
            style={{ maxWidth: "100%", marginTop: 10 }}
          />
        );
      }

      return (
        <video controls width="100%" height="140px" style={{ marginTop: 10 }}>
          <source src={file.preview} type="video/mp4" />
          <track kind="captions" srcLang="en" label="English captions" />
        </video>
      );
    }

    // Case 2: Existing asset media
    if (assetData?.asset_image) {
      const mediaUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${assetData.asset_image}`;
      const isPhoto = assetData.image_type === "Photo";

      if (isPhoto) {
        return (
          <img
            src={mediaUrl}
            alt="asset"
            style={{ maxWidth: "100%", marginTop: 10 }}
          />
        );
      }

      return (
        <video controls width="100%" height="140px" style={{ marginTop: 10 }}>
          <source src={mediaUrl} type="video/mp4" />
          <track kind="captions" srcLang="en" label="English captions" />
        </video>
      );
    }

    // Case 3: No media
    return null;
  };

  return (
    <SoftBox>
      <Formik<FormValues>
        initialValues={{
          address_id: assetData?.address_id ?? "",
          asset_name: assetData?.asset_name ?? "",
          asset_sku: assetData?.asset_sku ?? "",
          customer_id: assetData?.customer_id ?? "",
          division_id: assetData?.division_id ?? null,
          asset_purchased_date: assetData?.asset_purchased_date
            ? formatDate(assetData.asset_purchased_date)
            : "",
          asset_type_id: assetData?.asset_type_id ?? "",
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setSubmitting(true);
          try {
            // Upload file to S3 if exists
            if (uploadedFiles.length > 0) {
              const file = uploadedFiles[0];
              const fileName = `${Date.now()}-${file.name}`;
              values.asset_image = fileName;
              const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;
              if (!bucketName) {
                fetchError("AWS bucket configuration is missing");
                return;
              }
              const params: AWS.S3.PutObjectRequest = {
                Bucket: bucketName,
                Key: fileName,
                Body: file,
                ContentType: file.type,
                ACL: "public-read",
              };

              let type = file.type.split("/")[0];
              if (type === "image") type = "Photo";
              values.image_type = type;

              s3.upload(params, async (err, data) => {
                if (err) {
                  console.error(err);
                  setSubmitting(false);
                } else {
                  try {
                    const response = await axios.put(
                      `/assets/${assetData?.asset_id}`,
                      values
                    );
                    fetchSuccess(response.data.message);
                    fetchData();
                  } catch (error) {
                    if (error instanceof AxiosError && error.response) {
                      fetchError(error.response.data.error);
                    }
                  } finally {
                    setSubmitting(false);
                  }
                }
              });
            } else {
              const response = await axios.put(
                `/assets/${assetData?.asset_id}`,
                values
              );
              fetchSuccess(response.data.message);
              fetchData();
              setSubmitting(false);
            }
          } catch (error) {
            if (error instanceof AxiosError && error.response) {
              fetchError(error.response.data.error);
            }
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, values, setFieldValue }) => (
          <Form
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Customer
                  </SoftTypography>
                  <TextField
                    value={`${assetData?.customer_name} - (${assetData?.customer_phone})`}
                    variant="outlined"
                    fullWidth
                    disabled
                  />
                </SoftBox>
              </Grid>
              {assetData?.type === "company" && (
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Division
                    </SoftTypography>
                    <TextField
                      value={`${assetData?.division_name}`}
                      variant="outlined"
                      fullWidth
                      disabled
                    />
                  </SoftBox>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Asset Type
                </SoftTypography>
                <Autocomplete
                  options={assets ?? []}
                  getOptionLabel={(option) => option.asset_type_name ?? ""}
                  value={
                    assets.find(
                      (a) => a.asset_type_id === values.asset_type_id
                    ) ?? null
                  }
                  onChange={(e, value) => {
                    setFieldValue(
                      "asset_type_id",
                      value ? value.asset_type_id : ""
                    );
                    setFieldValue("asset_name", "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      error={!!errors.asset_type_id && touched.asset_type_id}
                      helperText={touched.asset_type_id && errors.asset_type_id}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Brand Name
                  </SoftTypography>
                  <Field
                    name="asset_name"
                    as={TextField}
                    fullWidth
                    variant="outlined"
                    error={!!errors.asset_name && touched.asset_name}
                    helperText={touched.asset_name && errors.asset_name}
                  />
                </SoftBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold">
                    SKU
                  </SoftTypography>
                  <Field
                    name="asset_sku"
                    as={TextField}
                    fullWidth
                    variant="outlined"
                    error={!!errors.asset_sku && touched.asset_sku}
                    helperText={touched.asset_sku && errors.asset_sku}
                  />
                </SoftBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Purchased Date
                </SoftTypography>
                <Field
                  name="asset_purchased_date"
                  type="date"
                  as={TextField}
                  fullWidth
                  variant="outlined"
                  InputProps={{ inputProps: { max: formattedToday } }}
                  error={
                    errors.asset_purchased_date && touched.asset_purchased_date
                  }
                  helperText={
                    touched.asset_purchased_date && errors.asset_purchased_date
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Address
                  </SoftTypography>
                  <TextField
                    fullWidth
                    value={`${assetData?.address_type}-${assetData?.city_name}-${assetData?.region_name}`}
                    variant="outlined"
                    disabled
                  />
                </SoftBox>
              </Grid>

              <Grid item xs={12}>
                <Box
                  {...dropzone.getRootProps()}
                  sx={{ cursor: "pointer", width: "fit-content" }}
                >
                  <input {...dropzone.getInputProps()} />
                  <Button variant="contained" color="primary">
                    Add/Upload File
                  </Button>
                </Box>

                {renderMediaPreview()}
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                className="success-btn"
                sx={{
                  position: "relative",
                  minWidth: 100,
                }}
                variant="contained"
                color="success"
                type="submit"
                disabled={submitting}
              >
                Update
              </Button>
              <Button
                className="cancel-btn"
                sx={{
                  position: "relative",
                  minWidth: 100,
                  ml: 2.5,
                }}
                variant="contained"
                color="warning"
                type="button"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </SoftBox>
  );
};

export default EditAsset;
